"use client"

import { useDictionary } from "@/core/hooks/use-dictionary";
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister, useWatch } from "react-hook-form";
import { MultilingualInput, MultilingualTextareaInput } from "../GlobalWidgetEditor";
import LinkSelector from "../../LinkSelector";
import { Label } from "@/components/ui/label";
import { Heading } from "@/components/ui/Heading";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import FileUploadInput from "../../input/MultipleFileUploadInput";

interface ScrollShowcaseWidgetProps {
    widgetIndex: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const ScrollShowcaseWidget = ({
    widgetIndex,
    name,
    control,
    register,
    errors,
    disabled,
}: ScrollShowcaseWidgetProps) => {
    const { dictionary } = useDictionary();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${name}.${widgetIndex}.items`,
    });

    // برای این‌که لیبل تصاویر بر اساس چیدمان انتخاب‌شده‌ی هر اسلاید عوض بشه
    const watchedItems = useWatch({ control, name: `${name}.${widgetIndex}.items` }) as
        | { imageLayout?: string }[]
        | undefined;

    const getError = (path: string) => {
        const errorPath = path.split('.');
        let current = errors;
        for (const key of errorPath) {
            if (current && typeof key === 'string' && key in current) {
                current = (current as any)[key];
            } else {
                return undefined;
            }
        }
        return (current as any)?.message;
    };

    const addItem = () => {
        append({
            label: '', label_en: '', label_ar: '',
            title: '', title_en: '', title_ar: '',
            description: '', description_en: '', description_ar: '',
            buttonText: '', buttonText_en: '', buttonText_ar: '',
            buttonLink: '',
            imagePosition: 'right',
            imageLayout: 'big_top',
            bigImage: undefined,
            smallImage1: undefined,
            smallImage2: undefined,
        });
    };

    const removeItem = (itemIndex: number) => {
        if (fields.length <= 1) return;
        remove(itemIndex);
    };

    const renderImageField = (itemIndex: number, field: 'bigImage' | 'smallImage1' | 'smallImage2', label: string) => {
        const imageError = getError(`${name}.${widgetIndex}.items.${itemIndex}.${field}`) as string | undefined;

        return (
            <div className="space-y-1">
                <label className="text-xs text-primary-600">{label}</label>
                <Controller
                    control={control}
                    name={`${name}.${widgetIndex}.items.${itemIndex}.${field}`}
                    render={({ field: imgField }) => (
                        <FileUploadInput
                            accept="image/*"
                            disabled={disabled}
                            onUploadSuccess={(image) => {
                                if (!Array.isArray(image) && image) imgField.onChange(image);
                            }}
                            defaultImages={
                                imgField.value
                                    ? [{ ...imgField.value, name: imgField.value.name || '', description: imgField.value.description || '' }]
                                    : []
                            }
                            onDefaultRemove={() => imgField.onChange(undefined)}
                            error={imageError}
                        />
                    )}
                />
            </div>
        );
    };

    const imageLayoutName = (itemIndex: number) => `${name}.${widgetIndex}.items.${itemIndex}.imageLayout`;
    const bigImageLabel = (layout: string) =>
        layout === 'tall_side' ? 'تصویر کشیده (نسبت پیشنهادی عمودی ۳:۴)' : 'تصویر بزرگ (نسبت پیشنهادی ۳:۲)';
    const smallImageLabel = (layout: string, which: 1 | 2) =>
        layout === 'tall_side'
            ? (which === 1 ? 'تصویر کوچک بالا (مربعی)' : 'تصویر کوچک پایین (مربعی)')
            : (which === 1 ? 'تصویر کوچک اول (مربعی)' : 'تصویر کوچک دوم (مربعی)');

    return (
        <>
            {fields.map((field, itemIndex) => (
                <div key={field.id} className="border col-span-2 p-4 rounded-md space-y-4 bg-background">
                    <div className="flex justify-between items-center">
                        <Heading level={4}>
                            اسلاید {itemIndex + 1}
                        </Heading>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeItem(itemIndex)}
                            disabled={fields.length <= 1 || disabled}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>

                    <MultilingualInput
                        baseName={`${name}.${widgetIndex}.items.${itemIndex}.label`}
                        label="برچسب کوچک بالای عنوان (مثلاً خوش‌آمدگویی)"
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />

                    <MultilingualInput
                        baseName={`${name}.${widgetIndex}.items.${itemIndex}.title`}
                        label={dictionary.forms?.title}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />

                    <MultilingualTextareaInput
                        baseName={`${name}.${widgetIndex}.items.${itemIndex}.description`}
                        label={dictionary.forms?.description}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 col-span-2">
                        <MultilingualInput
                            baseName={`${name}.${widgetIndex}.items.${itemIndex}.buttonText`}
                            label="متن دکمه (اختیاری)"
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />

                        <div className="space-y-1">
                            <Label className="text-xs font-medium text-muted-foreground">
                                {dictionary.forms?.link}
                            </Label>
                            <Controller
                                control={control}
                                name={`${name}.${widgetIndex}.items.${itemIndex}.buttonLink`}
                                render={({ field: linkField }) => (
                                    <LinkSelector
                                        value={linkField.value || ''}
                                        onChange={linkField.onChange}
                                        placeholder={dictionary.forms?.link}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 col-span-2">
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground">
                                سمت تصاویر
                            </label>
                            <Controller
                                control={control}
                                name={`${name}.${widgetIndex}.items.${itemIndex}.imagePosition`}
                                render={({ field: posField }) => (
                                    <Select
                                        value={posField.value || 'right'}
                                        onValueChange={posField.onChange}
                                        disabled={disabled}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="right">راست</SelectItem>
                                            <SelectItem value="left">چپ</SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground">
                                چیدمان تصاویر
                            </label>
                            <Controller
                                control={control}
                                name={imageLayoutName(itemIndex)}
                                render={({ field: layoutField }) => (
                                    <Select
                                        value={layoutField.value || 'big_top'}
                                        onValueChange={layoutField.onChange}
                                        disabled={disabled}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="big_top">بزرگ بالا + دو کوچیک پایین</SelectItem>
                                            <SelectItem value="tall_side">دو کوچیک روی هم + کشیده کنارش</SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 col-span-2">
                        {(() => {
                            const layout = watchedItems?.[itemIndex]?.imageLayout || 'big_top';
                            return (
                                <>
                                    <div className="md:col-span-2">
                                        {renderImageField(itemIndex, 'bigImage', bigImageLabel(layout))}
                                    </div>
                                    {renderImageField(itemIndex, 'smallImage1', smallImageLabel(layout, 1))}
                                    {renderImageField(itemIndex, 'smallImage2', smallImageLabel(layout, 2))}
                                </>
                            );
                        })()}
                    </div>
                </div>
            ))}

            <Button type="button" variant="outline" onClick={addItem} disabled={disabled} className="mt-2">
                <Plus className="h-4 w-4 mr-2" />
                {dictionary.common.add + " " + dictionary.forms.item}
            </Button>
        </>
    );
};
